import { lazy, Suspense, type ReactNode, type ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { AddressProvider } from "@/contexts/AddressContext";
import { BraceletGiftProvider } from "@/contexts/BraceletGiftContext";
import { AdminRoute } from "@/components/AdminRoute";
import { PageLoading } from "@/components/PageLoading";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { CartReminderActivity } from "@/components/CartReminders";
import { RouteMetadata } from "@/components/RouteMetadata";
import { AnalyticsChoice } from "@/components/AnalyticsChoice";
import { CrmActivity } from "@/components/CrmActivity";
import { adminPageModules } from "@/lib/adminPageModules";
import { AdminLayout } from "@/components/AdminLayout";

// The storefront home page stays in the entry chunk â€” it is the LCP route, so
// splitting it would add a round trip before the hero can even be requested.
import Index from "./pages/Index";
import { FestivalTheme } from "@/components/festival/FestivalTheme";

const Cart = lazy(() => import("./pages/Cart"));
const Auth = lazy(() => import("./pages/Auth"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Gemfinder = lazy(() => import("./pages/Gemfinder"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const CancellationRefunds = lazy(() => import("./pages/CancellationRefunds"));
const Profile = lazy(() => import("./pages/Profile"));
const Blogs = lazy(() => import("./pages/Blogs"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const VarahiNavaratrulu = lazy(() => import("./pages/campaigns/VarahiNavaratrulu"));
const PujaItems = lazy(() => import("./pages/PujaItems"));

const AdminNotifications = lazy(adminPageModules.notifications);
const AdminShipping = lazy(adminPageModules.shipping);
const AdminVideos = lazy(adminPageModules.videos);
const AdminDashboard = lazy(adminPageModules.dashboard);
const AdminOrders = lazy(adminPageModules.orders);
const AdminOrderDetails = lazy(adminPageModules.orderDetails);
const AdminProducts = lazy(adminPageModules.products);
const AdminInventory = lazy(adminPageModules.inventory);
const AdminFollowups = lazy(adminPageModules.followups);
const AdminGemFinderPayments = lazy(adminPageModules.gemfinderPayments);
const AdminBestsellers = lazy(adminPageModules.bestsellers);
const AdminBlogs = lazy(adminPageModules.blogs);
const AdminBanners = lazy(adminPageModules.banners);
const AdminReviews = lazy(adminPageModules.reviews);
const AdminCoupons = lazy(adminPageModules.coupons);
const AdminOffers = lazy(adminPageModules.offers);
const AdminBraceletGift = lazy(adminPageModules.braceletGift);
const AdminRecentPurchases = lazy(adminPageModules.recentPurchases);
const AdminGemfinder = lazy(adminPageModules.gemfinder);
const AdminAppPromotion = lazy(adminPageModules.appPromotion);
const AdminAccess = lazy(adminPageModules.access);

const queryClient = new QueryClient();

const CartReminderUnsubscribe = lazy(() => import("./pages/CartReminderUnsubscribe"));

const AdminWorkspace = () => {
  const location = useLocation();
  return (
    <AdminLayout independentScroll={location.pathname === '/admin/orders'}>
      <Suspense fallback={<div className="min-h-[40vh]" aria-label="Loading admin module" />}>
        <Outlet />
      </Suspense>
    </AdminLayout>
  );
};

function DefaultRouter({ children }: { children: ReactNode }) {
  return <BrowserRouter future={{ v7_relativeSplatPath: true }}>{children}</BrowserRouter>;
}

const App = ({ RouterHost = DefaultRouter, queryClient: activeQueryClient = queryClient }: { RouterHost?: ComponentType<{ children: ReactNode }>; queryClient?: QueryClient } = {}) => (
  <QueryClientProvider client={activeQueryClient}>
    <TooltipProvider>
      <AuthProvider>
        <AddressProvider>
          <WishlistProvider>
            <CartProvider>
              <BraceletGiftProvider>
              <Toaster />
              <Sonner />
              <AnalyticsChoice />
              <RouterHost>
                <FestivalTheme />
                <CartReminderActivity />
                <CrmActivity />
                <RouteMetadata />
                <RouteErrorBoundary>
                  <Suspense fallback={<PageLoading />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/category/:categorySlug" element={<CategoryPage />} />
                  <Route path="/videos" element={<Navigate to="/#watch-with-uma" replace />} />
                  <Route path="/gemfinder" element={<Gemfinder />} />
                  <Route path="/product/:categorySlug/:productSlug" element={<ProductDetail />} />
                  <Route path="/product/:productId" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/cart-reminders/unsubscribe" element={<CartReminderUnsubscribe />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  {import.meta.env.DEV && <Route path="/dev/payment-success" element={<OrderSuccess preview />} />}
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/order/:orderId" element={<OrderDetails />} />
                  <Route path="/terms-conditions" element={<TermsConditions />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/shipping-policy" element={<ShippingPolicy />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/cancellation-refunds" element={<CancellationRefunds />} />
                  <Route path="/blog" element={<Blogs />} />
                  <Route path="/blog/index.html" element={<Blogs />} />
                  <Route path="/blog/:language/:slug/:blogId" element={<BlogDetail />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />
                  <Route path="/guides/index.html" element={<Blogs />} />
                  <Route path="/guides/:slug" element={<BlogDetail />} />
                  <Route path="/campaigns/varahi-navaratrulu" element={<VarahiNavaratrulu />} />
                  <Route path="/puja-items" element={<PujaItems />} />

                  
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminWorkspace /></AdminRoute>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="shipping" element={<AdminShipping />} />
            <Route path="videos" element={<AdminVideos />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:orderId" element={<AdminOrderDetails />} />
            <Route path="orders/pending-payments" element={<AdminFollowups />} />
            <Route path="gemfinder/pending-payments" element={<AdminFollowups />} />
            <Route path="gemfinder/payments" element={<AdminGemFinderPayments />} />
            <Route path="followups" element={<AdminFollowups />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="bestsellers" element={<AdminBestsellers />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="banners" element={<AdminBanners />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="offers" element={<AdminOffers />} />
            <Route path="bracelet-gift" element={<AdminBraceletGift />} />
            <Route path="recent-purchases" element={<AdminRecentPurchases />} />
            <Route path="gemfinder" element={<AdminGemfinder />} />
            <Route path="app-promotion" element={<AdminAppPromotion />} />
            <Route path="access" element={<AdminAccess />} />
          </Route>
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </RouteErrorBoundary>
              </RouterHost>
              </BraceletGiftProvider>
            </CartProvider>
          </WishlistProvider>
        </AddressProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
